/**
 * Camera tuning (plan G9): rig look-ahead and roll lag, boost and speed FOV, screen shake. The values live in
 * camera.json; the parsed object is mutable so the dev panel (G14) can bind it live. `cameraTuning.rig` is the
 * very object every `CameraRig` reads, so a tweak applies on the next frame.
 */
import { z } from 'zod'
import cameraJson from './camera.json'

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()

export const cameraRigTuningSchema = z.object({
  /** Chase view aims at the point this many metres ahead of the ship's nose (0 = at the ship itself). */
  lookAhead: nonNegative,
  /**
   * Height of that aim point above the nose line, as a fraction of the ship's chaseOffset height: 1 keeps the
   * settled camera parallel to the nose (the far boresight at screen centre, the ship below it), 0 aims at the
   * nose line itself (the ship rides up toward the centre).
   */
  lookAheadHeight: z.number().min(0).max(2),
  /** Roll follows the ship at `chaseRotStiffness × rollLagScale` (below 1 = a softer horizon than pitch/yaw). */
  rollLagScale: positive,
  /** A chase lag larger than this (a respawn, a teleport) snaps instead of swinging around. */
  snapAngleDeg: z.number().min(1).max(180),
})

export const cameraFovTuningSchema = z.object({
  /** Extra vertical FOV at full boost. */
  boostKickDeg: nonNegative,
  /** Seconds to ease the kick in when boost starts, and out when it ends. */
  kickInSeconds: positive,
  kickOutSeconds: positive,
  /** Extra vertical FOV at the ship's top boosted speed. */
  speedMaxDeg: nonNegative,
  /** Speed FOV starts at this fraction of the top boosted speed and eases in up to it. */
  speedFromFraction: z.number().min(0).max(0.99),
  /** Exponential smoothing rate (1/s) of the speed FOV. */
  speedSmoothing: positive,
})

export const cameraShakeTuningSchema = z.object({
  /** Shake amplitude (degrees) added per point of damage the player takes on the hull. */
  degPerDamage: nonNegative,
  /** Multiplier on `degPerDamage` for damage the shield absorbs. */
  shieldScale: nonNegative,
  /** Amplitude cap (degrees). */
  maxDeg: nonNegative,
  /** Exponential decay rate (1/s) of the hit amplitude. */
  decayPerSecond: positive,
  /** Noise frequency of the hit shake. */
  frequencyHz: positive,
  /** Roll jitter relative to pitch and yaw. */
  rollScale: nonNegative,
  /** Shake multiplier in the cockpit view. */
  cockpitScale: nonNegative,
  /** Rumble amplitude (degrees) while boosting. */
  boostRumbleDeg: nonNegative,
  rumbleFrequencyHz: positive,
  /** Seconds for the rumble to ease in and out. */
  rumbleEaseSeconds: positive,
})

export const cameraTuningSchema = z.object({
  rig: cameraRigTuningSchema,
  fov: cameraFovTuningSchema,
  shake: cameraShakeTuningSchema,
})

export type CameraRigTuning = z.infer<typeof cameraRigTuningSchema>
export type CameraFovTuning = z.infer<typeof cameraFovTuningSchema>
export type CameraShakeTuning = z.infer<typeof cameraShakeTuningSchema>
export type CameraTuning = z.infer<typeof cameraTuningSchema>

export function parseCameraTuning(raw: unknown): CameraTuning {
  return cameraTuningSchema.parse(raw)
}

/** The live tuning, parsed once from camera.json. */
export const cameraTuning: CameraTuning = parseCameraTuning(cameraJson)
